import { Flex } from "@chakra-ui/layout";
import mapboxgl from "mapbox-gl";
import MapGL, { ErrorEvent, MapRef } from "react-map-gl/mapbox";
import { MapLayerMouseEvent } from "react-map-gl/maplibre";
import { ReactNode, RefObject, useCallback, useRef, useState } from "react";
import { Text, useColorMode } from "@chakra-ui/react";
import { useMapStyle } from "./Map.hooks";
import { MapLegendLayerToggle } from "./components";
import { ErrorBoundary, Loader } from "components";
import { WithChildren } from "types";

export const MAP_LEGEND_HEIGHT = 28;

type Props = {
  layersIds: string[];
  onClick: (e: MapLayerMouseEvent) => void;
  onMouseMove: (e: MapLayerMouseEvent) => void;
  isLoading: boolean;
  isGlobe?: boolean;
  height: string;
  mapRef: RefObject<MapRef>;
  renderLegend: () => ReactNode;
  renderSources: () => ReactNode;
  initialBounds?: [number, number, number, number] | undefined;
};

const Map = ({
  layersIds,
  onClick,
  onMouseMove,
  isGlobe = false,
  isLoading,
  height,
  mapRef,
  renderLegend,
  renderSources,
  initialBounds,
}: WithChildren<Props>) => {
  const { colorMode } = useColorMode();
  const mapStyle = useMapStyle(colorMode);
  const [hasInitError, setHasInitError] = useState(false);
  const isLoadedRef = useRef(false);

  const handleLoad = useCallback(() => {
    isLoadedRef.current = true;
  }, []);

  // react-map-gl instantiates the Mapbox map inside an async effect, so a
  // failure to create the WebGL context (e.g. "Failed to initialize WebGL." on
  // GPU-blocked, headless, or otherwise unsupported browsers) is delivered here
  // rather than thrown where the surrounding ErrorBoundary could catch it.
  // Without an onError handler react-map-gl falls back to console.error, which
  // Sentry's captureConsoleIntegration reports as noise. Treat a failure before
  // the map has loaded as fatal and render a contained fallback; ignore
  // transient post-load errors such as a single failed tile request.
  const handleError = useCallback((_event: ErrorEvent) => {
    if (!isLoadedRef.current) {
      setHasInitError(true);
    }
  }, []);

  if (isLoading) {
    return <Loader minHeight={0} height={height} />;
  }

  if (hasInitError) {
    return (
      <Flex
        height={height}
        direction="column"
        align="center"
        justify="center"
        textAlign="center"
        gap={1}
        p={4}
      >
        <Text fontSize="lg" fontWeight="bold">
          Unable to display the map
        </Text>
        <Text fontSize="sm" color="gray.500">
          Your browser or device could not initialize WebGL, which is required
          to render the map.
        </Text>
      </Flex>
    );
  }

  return (
    <ErrorBoundary>
      <Flex marginY={1}>{renderLegend()}</Flex>
      <MapGL
        mapLib={mapboxgl}
        projection={isGlobe ? "globe" : "mercator"}
        mapStyle={mapStyle}
        interactiveLayerIds={layersIds}
        attributionControl={false}
        style={{ width: "100%", height }}
        mapboxAccessToken="pk.eyJ1IjoiZ3RiOGtiYWR6djl3ZmEwdHlhIiwiYSI6ImNsZGVyZWViMzBiMDczcHQ1dm80N2pvZjEifQ.czm3-RCTbAQzrKCbdyP2-g"
        onMouseMove={onMouseMove}
        ref={mapRef}
        onClick={onClick}
        onLoad={handleLoad}
        onError={handleError}
        initialViewState={{
          bounds: initialBounds,
          fitBoundsOptions: {
            maxZoom: 5,
            padding: 50,
          },
        }}
      >
        {renderSources()}
      </MapGL>
    </ErrorBoundary>
  );
};

Map.MapLegendLayerToggle = MapLegendLayerToggle;

export { Map };
